<template>
  <form @submit.prevent="confirmUpdate()" action="">
    <ion-row>
      <ion-col size="12">
        <ion-list>
          <ion-item v-if="validationError" lines="none">
            <ion-label className="ion-text-wrap">
              <ion-text color="danger">
                <p v-for="error of v$.$errors" :key="error.$uid">
                  <ion-icon
                    :ios="alertCircleOutline"
                    :md="alertCircleSharp"
                    size="small"
                    style="vertical-align: middle"
                  ></ion-icon
                  >{{ error.$property }}: {{ error.$message }}
                </p></ion-text
              >
            </ion-label>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.subject_group_id.$error }">
            <ion-label position="floating">Subject Group</ion-label>
            <ion-select
              v-model="subject_group_id"
              :value="subject_group_id"
              @ionChange="updateName($event.target.value)"
            >
              <ion-select-option
                v-for="subject_group of subject_groups"
                :key="subject_group.id"
                :value="subject_group.id"
              >
                {{ subject_group.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.name.$error }">
            <ion-label position="floating">Name</ion-label>
            <ion-input type="text" v-model="name"></ion-input>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.standard_id.$error }">
            <ion-label position="floating">Standard</ion-label>
            <ion-select v-model="standard_id" :value="standard_id">
              <ion-select-option
                v-for="standard of standards"
                :key="standard.id"
                :value="standard.id"
              >
                {{ standard.name }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.is_mandatory.$error }">
            <ion-label position="floating">Is mandatory?</ion-label>
            <ion-select v-model="is_mandatory" :value="is_mandatory">
              <ion-select-option value="1"> Yes </ion-select-option>
              <ion-select-option value="0"> No </ion-select-option>
            </ion-select>
          </ion-item>

          <ion-item :class="{ 'color-danger': v$.assign_students.$error }">
            <ion-label position="floating">Assign all students?</ion-label>
            <ion-select
              v-model="assign_students"
              :value="assign_students"
              :disabled="is_mandatory == '1'"
            >
              <ion-select-option value="1"> Yes </ion-select-option>
              <ion-select-option value="0"> No </ion-select-option>
            </ion-select>
          </ion-item>
        </ion-list>
      </ion-col>
    </ion-row>
    <ion-row>
      <ion-col>
        <ion-button type="submit" expand="full" class="ion-margin-vertical">
          Save
        </ion-button>
      </ion-col>
    </ion-row>
  </form>
</template>

<script>
import {
  IonRow,
  IonCol,
  IonList,
  IonLabel,
  IonItem,
  IonIcon,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonText,
  loadingController,
  alertController,
} from "@ionic/vue";

import { alertCircleOutline, alertCircleSharp } from "ionicons/icons";

import {
  required,
  requiredIf,
  maxLength,
  integer,
  helpers,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

export default {
  props: {
    resource: {
      type: Object,
      required: true,
    },
  },
  emits: ["closeModal"],
  components: {
    IonRow,
    IonCol,
    IonList,
    IonLabel,
    IonItem,
    IonIcon,
    IonButton,
    IonSelect,
    IonSelectOption,
    IonInput,
    IonText,
  },
  data() {
    return {
      id: this.resource.id,
      name: this.resource.name,
      subject_group_id: null,
      standard_id: null,
      is_mandatory: this.resource.is_mandatory == true ? "1" : "0",
      assign_students: null,
      alertCircleOutline,
      alertCircleSharp,

      validationError: null,
      xhrError: {
        header: null,
        description: null,
      },
    };
  },

  computed: {
    subject_groups() {
      return this.$store.getters["subjectGroup/allResources"];
    },
    standards() {
      return this.$store.getters["standard/allResources"];
    },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  validations() {
    return {
      name: {
        required: helpers.withMessage("cannot be empty", required),
        maxLength: helpers.withMessage("maximum 32 characters", maxLength(32)),
      },

      subject_group_id: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
      },

      standard_id: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
      },

      is_mandatory: {
        required: helpers.withMessage("is required", required),
        integer: helpers.withMessage("can only be integers", integer),
      },
      assign_students: {
        requiredIfNotMandatory: helpers.withMessage(
          "is required",
          requiredIf(this.is_mandatory == "0")
        ),
        integer: helpers.withMessage("can only be integers", integer),
      },
    };
  },

  methods: {
    setModalState(state) {
      this.modalState = state;
    },

    loader(message) {
      const loading = loadingController.create({
        message: message,
        backdropDismiss: false,
      });

      return loading;
    },

    alerter(header, message, buttons = ["close"]) {
      const alert = alertController.create({
        header: header,
        message: message,
        buttons: buttons,
      });

      return alert;
    },

    setErrorResponse(error) {
      if (error.response) {
        this.xhrError.header = error.response.data.header;
        this.xhrError.message = error.response.data.message;
      } else if (error.request) {
        this.xhrError.header = error.message || error.request;
        this.xhrError.message = "Please check your connection and try again";
      } else {
        this.xhrError.header = error.message;
        this.xhrError.message = "Something went wrong. Try again later";
      }
    },

    async confirmUpdate() {
      const header = `Confirm`;
      const message = "Are you sure you want to save changes?";
      const buttons = [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "secondary",
        },
        {
          text: "Okay",
          handler: () => {
            this.updateSubject();
          },
        },
      ];
      const alertDialog = await this.alerter(header, message, buttons);

      alertDialog.present();
    },

    async updateSubject() {
      this.v$.$touch();
      if (this.v$.$error) {
        this.validationError = true;
        return;
      }

      let data = {
        id: this.id,
        name: this.name,
        subject_group_id: this.subject_group_id,
        standard_id: this.standard_id,
        is_mandatory: this.is_mandatory,
        assign_students: this.assign_students,
      };

      const spinner = await this.loader("Saving...");
      spinner.present();

      try {
        await this.$store.dispatch("subject/updateResource", data);

        /** Refresh Students list of updated subject */
        await this.$store.dispatch("subjectStudent/resources", {
          refresh: true,
          resourceId: this.id,
        });

        spinner.dismiss();

        this.name = null;
        this.$emit("closeModal");
      } catch (error) {
        spinner.dismiss();

        this.setErrorResponse(error);

        const alertDialog = await this.alerter(
          this.xhrError.header,
          this.xhrError.message
        );

        alertDialog.present();

        this.password = null;
        this.validationError = null;
      }
    },

    async fetchSubjectGroups() {
      await this.$store.dispatch("subjectGroup/allResources");
      this.subject_group_id = this.resource.subject_group_id;
    },
    async fetchStandards() {
      await this.$store.dispatch("standard/allResources");
      this.standard_id = this.resource.standard_id;
    },

    updateName(id) {
      const subject_group = this.subject_groups.filter((item) => {
        return item.id == id;
      });
      this.name = subject_group[0].name;
    },
  },

  created() {
    this.fetchSubjectGroups();
    this.fetchStandards();
  },
};
</script>